import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { PRODUCTS as STATIC_PRODUCTS, IMAGES } from "../data/products";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import Sparkle from "../components/Sparkle";
import { AlertTriangle } from "lucide-react";
import useSiteImages from "../hooks/useSiteImages";
import { API, resolveImageUrl } from "../lib/api";

const CATS = [
  { id: "all", label: "Everything" },
  { id: "cookies", label: "Cookies" },
  { id: "oatmeal", label: "Oatmeal" },
  { id: "brownies", label: "Brownies" },
];

export default function Menu() {
  const [cat, setCat] = useState("all");
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const siteImages = useSiteImages();

  // Pull the live catalog from the backend; fall back to static if API fails.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        if (mounted && Array.isArray(data) && data.length > 0) {
          // Resolve relative image URLs (admin-uploaded images return like "/api/images/{id}")
          const resolved = data.map((p) => ({
            ...p,
            image: resolveImageUrl(p.image),
          }));
          setProducts(resolved);
        }
      } catch (e) {
        // keep static fallback
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(
    () => (cat === "all" ? products : products.filter((p) => p.category === cat)),
    [cat, products]
  );

  return (
    <div data-testid="menu-page" className="bg-shay-cream">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={siteImages.menu_hero || IMAGES.parchment} alt="" className="w-full h-full object-cover opacity-30" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-b from-shay-cream via-shay-cream/85 to-shay-cream" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 text-center">
          <Reveal>
            <p className="eyebrow">The Menu</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] text-shay-navy mt-4 tracking-tight leading-[0.95]">
              Pick your <span className="italic text-shay-gold">favorite.</span>
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-shay-chocolate/80 text-lg leading-relaxed">
              Every cookie hand-rolled. Every brownie cut by hand. Every order baked the day it ships.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-30 bg-shay-cream/95 backdrop-blur-md border-y border-shay-gold/20">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="menu-filters">
          {CATS.map((c) => (
            <button
              key={c.id}
              data-testid={`menu-filter-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`px-6 py-2.5 rounded-full text-xs tracking-[0.22em] uppercase font-medium whitespace-nowrap transition-all duration-300 ${
                cat === c.id
                  ? "bg-shay-navy text-shay-gold"
                  : "border border-shay-navy/20 text-shay-navy hover:border-shay-gold hover:text-shay-gold"
              }`}
            >
              {c.label}
            </button>
          ))}
          <span className="ml-auto text-xs uppercase tracking-widest text-shay-chocolate/60 hidden md:block" data-testid="menu-product-count">
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-16 md:py-24" data-testid="menu-products">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.05}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Allergen — prominent */}
      <section className="bg-shay-navy text-shay-cream py-10 md:py-12 border-y-2 border-shay-gold/30" data-testid="menu-allergen-section">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="flex items-start gap-4 md:gap-6">
            <div className="hidden md:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-shay-gold/15 ring-1 ring-shay-gold/40">
              <AlertTriangle size={20} className="text-shay-gold" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <p className="eyebrow eyebrow-gold flex items-center gap-2">
                <Sparkle size={10} color="#C89A36" /> Allergen Notice
              </p>
              <p className="mt-3 font-display text-xl md:text-2xl leading-snug text-shay-cream">
                Our baked goods are made in a home kitchen where wheat, dairy, eggs, soy,
                peanuts, and tree nuts are used.
              </p>
              <p className="mt-3 text-shay-cream/75 leading-relaxed max-w-2xl">
                If you have a food allergy, please use caution, as we cannot guarantee that
                any item is completely free of allergens.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
